import { createFileRoute } from '@tanstack/react-router'
import type { CSSProperties } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Page } from '@/components/layout/page'
import { StaleDataHint } from '@/components/layout/offline-banner'
import { Skeleton } from '@/components/ui/skeleton'
import { api } from '@/api/http'

export const Route = createFileRoute('/mrgolisoda/outlet-gp/$outletId/')({
  component: OutletGPPage,
})

// ─── Types ────────────────────────────────────────────────────────────────────

interface GPRow {
  period: string
  target: string | number
  achieved: string | number
  pct: number
}

interface OutletGPResponse {
  client_id: number
  franchise_id: number
  date: string
  gp_performance: GPRow[]
  due_amount: string | number
  open_bills: number
  avg_weekly_rate: string | number
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function todayStr() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function inr(n: string | number) {
  const num = typeof n === 'string' ? parseFloat(n) : n
  return '₹' + Math.abs(num).toLocaleString('en-IN', { maximumFractionDigits: 0 })
}

function num(n: string | number) {
  return typeof n === 'string' ? parseFloat(n) : n
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const TH: CSSProperties = {
  padding: '6px 8px',
  fontSize: 11,
  fontWeight: 600,
  color: '#808080',
  borderBottom: '1px solid #E5E5E5',
  textAlign: 'right',
}

const TD: CSSProperties = {
  padding: '10px 8px',
  fontSize: 13,
  color: '#1A1A1A',
  fontVariantNumeric: 'tabular-nums',
  borderBottom: '1px solid #F0F0F0',
  textAlign: 'right',
}

// ─── Components ───────────────────────────────────────────────────────────────

function GPTable({ rows }: { rows: GPRow[] }) {
  const dataRows = rows.filter((r) => r.period !== 'Total')
  const totalRow = rows.find((r) => r.period === 'Total')

  function renderRow(r: GPRow, isTotal: boolean) {
    const target = num(r.target)
    const achieved = num(r.achieved)
    const isGood = r.pct >= 100
    const borderTop = isTotal ? '1.5px solid #E5E5E5' : undefined
    const borderBottom = isTotal ? 'none' : undefined
    return (
      <tr key={r.period} style={isTotal ? { fontWeight: 800 } : undefined}>
        <td style={{ ...TD, textAlign: 'left', fontWeight: isTotal ? 800 : 600, borderTop, borderBottom }}>
          {r.period}
        </td>
        <td style={{ ...TD, borderTop, borderBottom }}>{target > 0 ? inr(target) : '—'}</td>
        <td style={{ ...TD, borderTop, borderBottom }}>{achieved > 0 ? inr(achieved) : '—'}</td>
        <td style={{
          ...TD,
          fontWeight: 700,
          borderTop,
          borderBottom,
          color: target === 0 ? '#808080' : isGood ? '#226600' : achieved > 0 ? '#E65100' : '#808080',
        }}>
          {target > 0 ? `${r.pct}%` : '—'}
        </td>
      </tr>
    )
  }

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
      <thead>
        <tr>
          <th style={{ ...TH, textAlign: 'left', width: '25%' }}>Period</th>
          <th style={{ ...TH, width: '25%' }}>Target</th>
          <th style={{ ...TH, width: '27%' }}>Achieved</th>
          <th style={{ ...TH, width: '23%' }}>%</th>
        </tr>
      </thead>
      <tbody>
        {dataRows.map((r) => renderRow(r, false))}
        {totalRow && renderRow(totalRow, true)}
      </tbody>
    </table>
  )
}

function StatsGrid({ data }: { data: OutletGPResponse }) {
  const stats = [
    { label: 'Due amount', value: inr(data.due_amount) },
    { label: 'Open bills', value: String(data.open_bills) },
    { label: 'Avg weekly rate', value: inr(data.avg_weekly_rate) },
  ]
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, background: '#E5E5E5', borderRadius: 10, overflow: 'hidden', marginTop: 12 }}>
      {stats.map((s) => (
        <div key={s.label} style={{ background: '#F8F8F8', padding: '10px 8px', textAlign: 'center' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1A1A1A' }}>{s.value}</div>
          <div style={{ fontSize: 10, color: '#808080', marginTop: 2 }}>{s.label}</div>
        </div>
      ))}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function OutletGPPage() {
  const { outletId } = Route.useParams()
  const date = todayStr()

  const { data, isLoading, isPaused, error } = useQuery({
    queryKey: ['outlet-gp', outletId, date],
    queryFn: () =>
      api.get<OutletGPResponse>(
        `/field-customization/goli-soda/outlet-gp/?client_id=${outletId}&date=${date}`,
      ),
  })

  return (
    <Page noHeader scaled>
      {isPaused && data && <StaleDataHint />}
      <div style={{ padding: '16px 16px 40px', background: '#F5F5F5', minHeight: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}>

        {isLoading && (
          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #EBEBEB', padding: 14 }}>
            <Skeleton className="h-8 w-32 mb-4" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-16 w-full mt-3" />
          </div>
        )}

        {error && !data && !isPaused && (
          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #EBEBEB', padding: 14 }}>
            <p style={{ fontSize: 13, color: '#E51414', textAlign: 'center' }}>
              {(error as Error).message}
            </p>
          </div>
        )}

        {data && (
          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #EBEBEB', padding: 14 }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: '#1A1A1A', marginBottom: 12 }}>
              GP Performance
            </p>
            <GPTable rows={data.gp_performance} />
            <StatsGrid data={data} />
          </div>
        )}

      </div>
    </Page>
  )
}
